import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Plus, Server, Trash2, Edit, X, Download, Upload, FileText, Briefcase, ChevronDown, Eye } from 'lucide-react'
import Papa from 'papaparse'
import { ConfirmModal } from '../components/ConfirmModal'

const ASSET_TYPES = ['Hardware', 'Software', 'Data', 'People', 'Vendor', 'Facility']
const CLASSIFICATIONS = ['Public', 'Internal', 'Confidential', 'Strictly Confidential']

function AssetViewModal({ asset, processes = [], onClose, onEdit }) {
  const parseJSON = (str) => {
    try { return JSON.parse(str || '[]') } catch { return [] }
  }

  const linkedProcessIds = parseJSON(asset.associated_processes).map(Number)
  const linkedProcesses = processes.filter(p => linkedProcessIds.includes(p.id))

  const classColor = (c) => ({
    'Public': 'badge-success', 'Internal': 'badge-info',
    'Confidential': 'badge-warning', 'Strictly Confidential': 'badge-danger'
  }[c] || 'badge-neutral')

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'var(--accent)22', color: 'var(--accent)', padding: 8, borderRadius: 8 }}>
              <Server size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Asset Details</h2>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                AST-{String(asset.id).padStart(4, '0')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline btn-sm" onClick={onEdit}><Edit size={14} /> Edit</button>
            <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <label className="form-label">Asset Name</label>
              <p style={{ fontSize: '1.125rem', fontWeight: 600, margin: '4px 0 0 0' }}>{asset.asset_name}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <label className="form-label">Classification</label>
              <p style={{ margin: '4px 0 0 0' }}><span className={`badge ${classColor(asset.classification)}`}>{asset.classification}</span></p>
            </div>
          </div>

          <div className="grid-3" style={{ marginBottom: '20px' }}>
            <div>
              <label className="form-label">Type</label>
              <p style={{ margin: '4px 0 0 0' }}><span className="badge badge-neutral">{asset.asset_type}</span></p>
            </div>
            <div>
              <label className="form-label">Owner</label>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{asset.owner || '—'}</p>
            </div>
            <div>
              <label className="form-label">Location</label>
              <p style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{asset.location || '—'}</p>
            </div>
          </div>

          <div style={{ background: 'var(--bg-base)', borderRadius: 'var(--radius)', padding: 16, border: '1px solid var(--border)', marginBottom: 20 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 12, letterSpacing: '0.04em', textTransform: 'uppercase' }}>CIA Rating (1–5)</div>
            <div className="grid-3">
              <div>
                <label className="form-label">Confidentiality</label>
                <div style={{ marginTop: 4 }}><CIABadge score={asset.confidentiality} /></div>
              </div>
              <div>
                <label className="form-label">Integrity</label>
                <div style={{ marginTop: 4 }}><CIABadge score={asset.integrity} /></div>
              </div>
              <div>
                <label className="form-label">Availability</label>
                <div style={{ marginTop: 4 }}><CIABadge score={asset.availability} /></div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Linked Processes</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {linkedProcesses.map(p => (
                <span key={p.id} className="badge badge-neutral" style={{ fontSize: '0.75rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Briefcase size={12} />
                  {p.process_name}
                </span>
              ))}
              {linkedProcesses.length === 0 && <span className="text-muted" style={{ fontSize: '0.875rem' }}>—</span>}
            </div>
          </div>

          <div>
            <label className="form-label">Description</label>
            <p className="text-muted" style={{ whiteSpace: 'pre-wrap', background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.875rem' }}>
              {asset.description || 'No description provided.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function CIABadge({ score }) {
  const color = score >= 4 ? 'var(--risk-critical)' : score >= 3 ? 'var(--risk-medium)' : 'var(--risk-low)'
  return <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color, fontWeight: 600 }}>{score}</span>
}

function AssetModal({ asset, processes = [], onClose, onSave, isSaving }) {
  const parseJSON = (str) => {
    try { return JSON.parse(str || '[]') } catch { return [] }
  }
  const [form, setForm] = useState(() => {
    if (asset) {
      return { ...asset, associated_processes: parseJSON(asset.associated_processes).map(Number) }
    }
    return {
      asset_name: '', asset_type: 'Software', owner_ids: [], location: '',
      classification: 'Internal', confidentiality: 3, integrity: 3, availability: 3,
      description: '', associated_processes: []
    }
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const [processSearch, setProcessSearch] = useState('')
  const [showProcessResults, setShowProcessResults] = useState(false)

  const toggleProcess = (id) => {
    const pid = Number(id)
    setForm(prev => {
      const exists = prev.associated_processes.includes(pid)
      const next = exists ? prev.associated_processes.filter(x => x !== pid) : [...prev.associated_processes, pid]
      return { ...prev, associated_processes: next }
    })
  }

  const filteredProcesses = processes.filter(p =>
    !form.associated_processes.includes(p.id) &&
    p.process_name.toLowerCase().includes(processSearch.toLowerCase())
  ).slice(0, 10)

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{asset ? 'Edit Asset' : 'Register New Asset'}</h2>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Asset Name *</label>
              <input className="form-input" value={form.asset_name} onChange={e => set('asset_name', e.target.value)} placeholder="e.g. CRM Database" />
            </div>
            <div className="form-group">
              <label className="form-label">Asset Type *</label>
              <select className="form-select" value={form.asset_type} onChange={e => set('asset_type', e.target.value)}>
                {ASSET_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Owner</label>
              <input className="form-input" value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="e.g. John Smith" />
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. AWS us-east-1" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Classification</label>
            <select className="form-select" value={form.classification} onChange={e => set('classification', e.target.value)}>
              {CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div style={{ background: 'var(--bg-base)', borderRadius: 'var(--radius)', padding: 16, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 12, letterSpacing: '0.04em', textTransform: 'uppercase' }}>CIA Rating (1–5)</div>
            <div className="grid-3">
              {[['confidentiality', 'C — Confidentiality'], ['integrity', 'I — Integrity'], ['availability', 'A — Availability']].map(([key, label]) => (
                <div className="form-group" key={key}>
                  <label className="form-label">{label}</label>
                  <select className="form-select" value={form[key]} onChange={e => set(key, Number(e.target.value))}>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* LINKED PROCESSES */}
          <div className="form-group" style={{ marginTop: 20 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Briefcase size={13} /> Linked Processes
            </label>
            <div className="ctrl-search-container" style={{ position: 'relative' }}>
              <div 
                className="form-input flex items-center justify-between cursor-pointer"
                style={{ background: 'var(--bg-elevated)', minHeight: 42, flexWrap: 'wrap', gap: 6, padding: '6px 12px' }}
                onClick={() => setShowProcessResults(!showProcessResults)}
              >
                {form.associated_processes.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {form.associated_processes.map(pid => {
                      const p = processes.find(x => x.id === pid)
                      return (
                        <span key={pid} className="badge badge-neutral" style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                          <Briefcase size={10} />
                          {p?.process_name || `ID-${pid}`}
                          <X size={10} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); toggleProcess(pid) }} />
                        </span>
                      )
                    })}
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>Link processes to this asset...</span>
                )}
                <ChevronDown size={16} style={{ opacity: 0.5, marginLeft: 'auto' }} />
              </div>

              {showProcessResults && (
                <div className="ctrl-search-results shadow-xl" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 101, background: 'var(--bg-modal)', border: '1px solid var(--border)', borderRadius: 12, marginTop: 8, maxHeight: 250, overflowY: 'auto' }}>
                  <div style={{ padding: 10, borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--bg-modal)' }}>
                    <input 
                      autoFocus
                      className="form-input" 
                      placeholder="Search processes..." 
                      value={processSearch} 
                      onChange={e => setProcessSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ height: 32, fontSize: '0.8125rem' }}
                    />
                  </div>
                  <div style={{ padding: '4px 0' }}>
                    {filteredProcesses.length > 0 ? filteredProcesses.map(p => (
                      <div 
                        key={p.id} 
                        className="ctrl-search-item" 
                        style={{ padding: '8px 16px', cursor: 'pointer', transition: 'background 0.2s', borderBottom: '1px solid var(--border-light)' }}
                        onClick={(e) => { e.stopPropagation(); toggleProcess(p.id); setProcessSearch(''); setShowProcessResults(false) }}
                      >
                        <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{p.process_name}</div>
                        {p.description && <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{p.description.slice(0, 60)}</div>}
                      </div>
                    )) : (
                      <div style={{ padding: 15, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No processes found.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of this asset and its role…" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!form.asset_name || isSaving} onClick={() => onSave(form)}>
            {isSaving ? 'Saving…' : asset ? 'Update Asset' : 'Register Asset'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function Assets({ toast }) {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [viewing, setViewing] = useState(null)
  const [filterType, setFilterType] = useState('All')
  const [sortBy, setSortBy] = useState('id_asc')
  const [confirmDelete, setConfirmDelete] = useState(null)

  const { data: assets = [], isLoading } = useQuery({ queryKey: ['assets'], queryFn: api.getAssets })
  const { data: processes = [] } = useQuery({ queryKey: ['processes'], queryFn: api.getProcesses })

  const createMut = useMutation({
    mutationFn: api.createAsset,
    onSuccess: () => { qc.invalidateQueries(['assets']); setShowModal(false); toast('Asset registered', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateAsset(id, data),
    onSuccess: () => { qc.invalidateQueries(['assets']); setEditing(null); toast('Asset updated', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  const deleteMut = useMutation({
    mutationFn: ({ id, reason }) => api.deleteAsset(id, reason),
    onSuccess: () => { qc.invalidateQueries(['assets']); toast('Asset moved to bin', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const bulkCreateMut = useMutation({
    mutationFn: api.bulkCreateAssets,
    onSuccess: (res) => { 
      qc.invalidateQueries(['assets'])
      toast(`${res.count} assets uploaded successfully`, 'success') 
    },
    onError: e => toast(e.message, 'error'),
  })

  const exportToCSV = () => {
    const data = assets.map(a => ({
      'Asset ID': `AST-${String(a.id).padStart(4, '0')}`,
      'Asset Name': a.asset_name,
      'Asset Type': a.asset_type,
      'Owner': a.owner,
      'Location': a.location,
      'Classification': a.classification,
      'Confidentiality': a.confidentiality,
      'Integrity': a.integrity,
      'Availability': a.availability,
      'Description': a.description
    }))
    const csv = Papa.unparse(data)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', `Asset_Register_${new Date().toISOString().split('T')[0]}.csv`)
    link.click()
  }

  const downloadTemplate = () => {
    const template = [{
      'asset_name': 'Example Server',
      'asset_type': 'Hardware',
      'owner': 'IT Team',
      'location': 'Data Center A',
      'classification': 'Confidential',
      'confidentiality': 4,
      'integrity': 4,
      'availability': 5,
      'description': 'Main production web server'
    }]
    const csv = Papa.unparse(template)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', 'Asset_Bulk_Upload_Template.csv')
    link.click()
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data.map(row => ({
          ...row,
          confidentiality: parseInt(row.confidentiality || row['Confidentiality']) || 3,
          integrity: parseInt(row.integrity || row['Integrity']) || 3,
          availability: parseInt(row.availability || row['Availability']) || 3
        }))
        bulkCreateMut.mutate(data)
      }
    })
    e.target.value = ''
  }

  const filtered = filterType === 'All' ? assets : assets.filter(a => a.asset_type === filterType)
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'id_asc') return a.id - b.id
    if (sortBy === 'id_desc') return b.id - a.id
    if (sortBy === 'name_asc') return a.asset_name.localeCompare(b.asset_name)
    if (sortBy === 'name_desc') return b.asset_name.localeCompare(a.asset_name)
    return 0
  })

  const classColor = (c) => ({
    'Public': 'badge-success', 'Internal': 'badge-info',
    'Confidential': 'badge-warning', 'Strictly Confidential': 'badge-danger'
  }[c] || 'badge-neutral')

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Asset Register</h1>
          <p>All organizational assets with CIA ratings and classification</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={downloadTemplate}>
            <Download size={15} /> Template
          </button>
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <FileText size={15} /> Export CSV
          </button>
          <div style={{ position: 'relative' }}>
            <input 
              type="file" 
              accept=".csv" 
              onChange={handleFileUpload} 
              style={{ position: 'absolute', opacity: 0, inset: 0, cursor: 'pointer' }} 
            />
            <button className="btn btn-secondary">
              <Upload size={15} /> Bulk Upload
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={15} /> Register Asset
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['All', ...ASSET_TYPES].map(t => (
            <button
              key={t}
              className={`btn btn-sm ${filterType === t ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilterType(t)}
            >
              {t} {t !== 'All' && <span style={{ opacity: 0.7 }}>({assets.filter(a => a.asset_type === t).length})</span>}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Sort by:</span>
          <select className="form-select" style={{ height: 32, padding: '0 28px 0 8px', fontSize: '0.8125rem', width: 'auto' }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="id_asc">ID (Ascending)</option>
            <option value="id_desc">ID (Descending)</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        {ASSET_TYPES.map(type => {
          const count = assets.filter(a => a.asset_type === type).length
          return (
            <div key={type} className="stat-card" style={{ padding: '16px 20px' }}>
              <div className="stat-label">{type}</div>
              <div className="stat-value" style={{ fontSize: '1.5rem' }}>{count}</div>
            </div>
          )
        })}
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Asset ID</th>
              <th>Name</th>
              <th>Type</th>
              <th>Owner</th>
              <th>Classification</th>
              <th style={{ textAlign: 'center' }}>C</th>
              <th style={{ textAlign: 'center' }}>I</th>
              <th style={{ textAlign: 'center' }}>A</th>
              <th>Location</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10}><div className="empty-state" style={{ padding: '32px 0' }}><p>Loading…</p></div></td></tr>
            )}
            {!isLoading && sorted.length === 0 && (
              <tr><td colSpan={10}>
                <div className="empty-state" style={{ padding: '48px 0' }}>
                  <Server size={32} />
                  <p>No assets registered yet. Click "Register Asset" to begin.</p>
                </div>
              </td></tr>
            )}
            {sorted.map(a => (
              <tr key={a.id}>
                <td><span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>AST-{String(a.id).padStart(4,'0')}</span></td>
                <td style={{ fontWeight: 500 }}>{a.asset_name}</td>
                <td><span className="badge badge-neutral">{a.asset_type}</span></td>
                <td style={{ color: 'var(--text-secondary)' }}>{a.owner || '—'}</td>
                <td><span className={`badge ${classColor(a.classification)}`}>{a.classification}</span></td>
                <td style={{ textAlign: 'center' }}><CIABadge score={a.confidentiality} /></td>
                <td style={{ textAlign: 'center' }}><CIABadge score={a.integrity} /></td>
                <td style={{ textAlign: 'center' }}><CIABadge score={a.availability} /></td>
                <td style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{a.location || '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={() => setViewing(a)} title="View Asset">
                      <Eye size={14} />
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={() => setEditing(a)} title="Edit Asset">
                      <Edit size={14} />
                    </button>
                    <button className="btn btn-ghost btn-sm text-danger" style={{ padding: 6 }} onClick={() => setConfirmDelete(a)} title="Delete Asset">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && <AssetModal processes={processes} onClose={() => setShowModal(false)} onSave={d => createMut.mutate(d)} isSaving={createMut.isPending} />}
      {editing && <AssetModal asset={editing} processes={processes} onClose={() => setEditing(null)} onSave={d => updateMut.mutate({ id: editing.id, data: d })} isSaving={updateMut.isPending} />}
      {viewing && (
        <AssetViewModal
          asset={viewing}
          processes={processes}
          onClose={() => setViewing(null)}
          onEdit={() => { setEditing(viewing); setViewing(null); }}
        />
      )}

      <ConfirmModal 
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={(reason) => deleteMut.mutate({ id: confirmDelete.id, reason })}
        title="Move to Bin?"
        message={`Delete "${confirmDelete?.asset_name}"? It will be moved to the Recycle Bin and can be restored later.`}
        showReason={true}
      />
    </div>
  )
}
